import { BackTop, ConfigProvider, Layout, Row, message } from "antd";
import { ConnectedRouter } from "connected-react-router";
import * as moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import ReactGA from "react-ga";
import { hot } from "react-hot-loader/root";
import Joyride, { CallBackProps } from "react-joyride";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Route, Switch } from "react-router-dom";
import { useMedia } from "react-use";
import * as actions from "./actions";
import Nav from "./components/nav";
import PrivateRoute from "./components/privateroute";
import DealerPortalAccess from "./pages/dealerportalaccess";
import Details from "./pages/details";
import ForgotPasswordPage from "./pages/forgotpassword";
import Home from "./pages/home";
import LoginPage from "./pages/login";
import Welcome from "./pages/welcome";
import { history } from "./stores/configureStore";
// import { useWhyDidYouUpdate } from "./hooks/useWhyDidYouUpdate";

import TagManager, { TagManagerArgs } from "react-gtm-module";
import { State } from "./interfaces";
import { AuthState, User } from "./reducers/auth";
import { FiltersState } from "./reducers/filters";
import { ItemsState } from "./reducers/items";
import { LanguageState } from "./reducers/language";
import { ShareState } from "./reducers/share";
import { detectIE11 } from "./utils";

const { Content } = Layout;

const REACT_APP_GOOGLE_TAG_MANAGER_ID =
  process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID;

const tagManagerArgs: TagManagerArgs = {
  gtmId: REACT_APP_GOOGLE_TAG_MANAGER_ID as string,
  dataLayerName: "dataLayer"
};

TagManager.initialize(tagManagerArgs);

const App = () => {
  const auth: AuthState = useSelector(
    (state: State) => state.auth,
    shallowEqual
  );
  const {
    user,
    hasError: loginHasError,
    errorMessage: loginError,
    forgotPasswordMessage
  } = auth;

  console.log("user", user);

  const filters: FiltersState = useSelector(
    (state: State) => state.filters,
    shallowEqual
  );
  const { hasError: filtersHasError, errorMessage: filtersError } = filters;

  const _language: LanguageState = useSelector(
    (state: State) => state.language,
    shallowEqual
  );
  const {
    labels,
    selectedLanguage,
    hasError: languageHasError,
    errorMessage: languageError
  } = _language;

  const items: ItemsState = useSelector(
    (state: State) => state.items,
    shallowEqual
  );
  const { hasError: itemsHasError, errorMessage: itemsError } = items;

  const share: ShareState = useSelector(
    (state: State) => state.share,
    shallowEqual
  );

  const { hasError: shareHasError, errorMessage: shareError } = share;

  const dispatch = useDispatch();

  const onForgotPasswordSuccess = useCallback(
    (path) => dispatch(actions.redirectToPage(path)),
    [dispatch]
  );

  const [locale, setLocale] = useState(undefined);
  const [run, setRun] = useState(false);
  // const [steps, setSteps] = useState<{
  //     target: string;
  //     content: string;
  //     placement: string;
  //   }[]>([]);
  const [steps, setSteps] = useState<any[]>([]);
  const [localeChanged, setLocaleChanged] = useState(false);
  const [language, setLanguage] = useState("");

  const isSmall = useMedia("(max-width: 768px)");

  const isIE11 = detectIE11();

  // useWhyDidYouUpdate("App", props);

  useEffect(() => {
    // ReactGA
    // avvio analytics per questa pagina
    ReactGA.pageview(history.location.pathname);
    // e tutte le pagine successive
    history.listen((location, action) => {
      // ReactGA.set({ page: location.pathname });
      ReactGA.pageview(location.pathname);
      // console.log("location", location.pathname);
      // salvo la location su localStorage
      localStorage.setItem("from", location.pathname);
    });
  }, []);

  useEffect(() => {
    // componentDidUpdate
    // console.log("componentDidUpdate");

    if (loginHasError) {
      message.error(loginError);
      refreshXCsrfToken(loginError);
    }
    if (filtersHasError) {
      message.error(filtersError);
      refreshXCsrfToken(filtersError);
    }
    if (itemsHasError) {
      message.error(itemsError);
      refreshXCsrfToken(itemsError);
    }
    if (languageHasError) {
      message.error(languageError);
      refreshXCsrfToken(languageError);
    }
    if (shareHasError) {
      message.error(shareError);
      refreshXCsrfToken(shareError);
    }
    if (forgotPasswordMessage && labels) {
      message.success(labels.recoverPasswordSuccessMessage, 2, () =>
        onForgotPasswordSuccess("/")
      );
    }
  }, [
    filtersError,
    filtersHasError,
    forgotPasswordMessage,
    itemsError,
    itemsHasError,
    labels,
    languageError,
    languageHasError,
    loginError,
    loginHasError,
    onForgotPasswordSuccess,
    shareError,
    shareHasError
  ]);

  useEffect(() => {
    // se cambia la lingua importo il nuovo locale
    console.log("selectedLanguage ", selectedLanguage);
    if (selectedLanguage && selectedLanguage.id !== language) {
      _loadAsyncData(selectedLanguage!.id);
    }
  }, [language, selectedLanguage]); // getDerivedStateFromProps

  // se il locale è cambiato devo settare joyride con i testi in lingua
  if (labels && localeChanged) {
    // console.log("joyride con i testi in lingua");
    // Retrieve
    const hideHelp = localStorage.getItem("hideHelp");
    // console.log("hideHelp ", hideHelp);
    if (hideHelp !== "true") {
      const content = labels.helpText;
      setRun(true);
      setLocaleChanged(false);
      setSteps([
        {
          target: ".help",
          content,
          placement: "left"
        }
      ]);
    }
  }

  const refreshXCsrfToken = (_message: string | null) => {
    if (_message?.indexOf("X-CSRF-Token") !== -1) {
      console.log(_message);
    }
  };

  const _loadAsyncData = async (id: string) => {
    // console.log("_loadAsyncData id ", id);
    let results = [undefined, undefined];
    switch (id) {
      case "it":
        results = await Promise.all([
          import("antd/lib/locale-provider/it_IT"),
          import("moment/locale/it")
        ]);
        break;

      case "es":
        results = await Promise.all([
          import("antd/lib/locale-provider/es_ES"),
          import("moment/locale/es")
        ]);
        break;

      case "fr":
        results = await Promise.all([
          import("antd/lib/locale-provider/fr_FR"),
          import("moment/locale/fr")
        ]);
        break;

      case "de":
        results = await Promise.all([
          import("antd/lib/locale-provider/de_DE"),
          import("moment/locale/de")
        ]);
        break;

      default:
        results = [undefined, undefined];
    }
    // destrutturo i risultati
    const [antdLocale] = results;

    moment.locale(id);
    setLocale(
      antdLocale !== undefined ? (antdLocale as any)!.default : undefined
    );
    setLocaleChanged(true);
    setLanguage(id);
  };

  const helpCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === "finished") {
      // console.log("data ", data);
      localStorage.setItem("hideHelp", "true");
    }
  };

  return (
    <ConfigProvider locale={locale}>
      <ConnectedRouter history={history}>
        <Layout
          className={user ? "" : "not-logged-in"}
          style={{ minHeight: "100vh" }}
        >
          {!isSmall && !isIE11 && (
            <Joyride
              steps={steps}
              run={run}
              styles={{
                options: {
                  primaryColor: "#e68f1a"
                }
              }}
              callback={helpCallback}
            />
          )}
          <Nav />
          <BackTop />
          <Content className="main-content">
            <Switch>
              <Route exact path="/" component={LoginPage} />
              <Route
                exact
                path="/forgot-password"
                component={ForgotPasswordPage}
              />
              <PrivateRoute
                exact
                path="/welcome"
                component={Welcome}
                user={user as User}
              />
              <PrivateRoute
                path="/(home|details)"
                component={Home}
                user={user as User}
              />
              <Route
                exact
                path="/dealer/:access_token/:refresh_token"
                component={DealerPortalAccess}
              />
              <Route component={NoMatch} />
            </Switch>
            <PrivateRoute
              path="/details/:itemId"
              component={Details}
              user={user as User}
            />
          </Content>
          {/* <Footer style={{ textAlign: "center" }}>
            © 2017 CNH Industrial America LLC. Tutti i diritti riservati CASE e
            CNH Capital sono marchi registrati di CNH Industrial America LLC.
          </Footer> */}
        </Layout>
      </ConnectedRouter>
    </ConfigProvider>
  );
};

const NoMatch = ({ location }: any) => (
  <Row align="middle" justify="center">
    <h3>
      No match for <code>{location.pathname}</code>
    </h3>
  </Row>
);

export default process.env.NODE_ENV === "development" ? hot(App) : App;
